@file:OptIn(ExperimentalTime::class)

package net.sergeych.ugdx.shared.trader

import io.ktor.util.*
import kotlinx.coroutines.*
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.*
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.debug
import net.sergeych.mp_tools.globalLaunch
import net.sergeych.mptools.Now
import net.sergeych.sprintf.format
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.realtime.RealtimeChannel
import net.sergeych.ugdx.shared.api.PushPayload
import net.sergeych.ugdx.shared.api.TraderStatsArgs
import net.sergeych.ugdx.shared.api.cmdTraderGetStats
import net.sergeych.ugdx.shared.api.invoke
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime


class Stats private constructor(
    val currencyPair: CurrencyPair,
    val rate: StatSampleRate,
    val length: Int,
) {

    private val sticks = mutableListOf<Pair<Instant, CandleStick>>()

    private var lastPeriod: Period = rate.unit.closestInPast()

    private var cachedFlow: MutableStateFlow<List<Pair<Instant, CandleStick>>>? = null
    private val mutex = Mutex()

    private suspend fun sticksFlow(): MutableStateFlow<List<Pair<Instant, CandleStick>>> {
        return mutex.withLock {
            fill()
            cachedFlow ?: MutableStateFlow(sticks.toList()).also { f ->
                cachedFlow = f
                globalLaunch {
                    while (isActive) {
                        delay(5000)
                        if (fill())
                            f.value = sticks.toList()
                    }
                }
            }
        }
    }

    private suspend fun fill(): Boolean =
        if (sticks.size >= length) {
            // refresh if possible
            val nextPeriod = rate.unit.closestInPast()
            if (lastPeriod != nextPeriod) {
                load(nextPeriod.startsAt, 1).firstOrNull()?.let { p ->
                    if (p.first >= lastPeriod.endsAt) {
                        sticks.removeLast()
                        sticks.add(0, p)
                        lastPeriod = nextPeriod
                        true
                    } else false // refresh returned the same value - what is actually very strange
                } ?: false // load failed
            } else false // too early
        } else {
            // loadAll
            sticks.clear()
            sticks.addAll(load(lastPeriod.startsAt, length))
            true
        }

    private suspend fun load(from: Instant, count: Int): List<Pair<Instant, CandleStick>> =
        // get stats from now using current nearest time and size
        cmdTraderGetStats.invoke(TraderStatsArgs(currencyPair, from, rate, count)).sticks

    companion object : LogTag("SSTATS") {
        /**
         * Get the state flow of stats for a given parameters. Collect the flow, can cancel collecting job when no
         * more needed. It is appropriately cached.
         *
         * Note. This function does not return incomplete, dynamic first stick.
         *
         * Recommendation: use [sticksFlow] instead.
         */
        suspend fun flowOf(currencyPair: CurrencyPair, rate: StatSampleRate, length: Int)
                : MutableStateFlow<List<Pair<Instant, CandleStick>>> =
            Stats(currencyPair, rate, length).sticksFlow()

        /**
         * Get the flow of jumping sticks (incomplete stick for the current yet incomplete period). To properly
         * process this flow, make sure that updated stick is really adjacent to the data you are showing to user,
         * as it _may not be in sync with stats from [flowOf]_. There is no guarantee that [flowOf] update will
         * always preceede [lastStickFlowOf], so extra care is required. If the stick is "too new" for the data
         * you show, save to and use when the corresponding stats update arrive.
         *
         * Recommendation: use [sticksFlow] instead.
         */
        suspend fun lastStickFlowOf(currencyPair: CurrencyPair, rate: StatSampleRate)
                : Flow<PushPayload.JumpingCandleStick> {
            RealtimeChannel.watchPair(currencyPair, true)
            return flow {
                val last = RealtimeChannel.getLastStick(currencyPair, rate)
                emit(last)
                try {
                    GuestAPI().pushes.collect {
                        if (it is PushPayload.JumpingCandleStick && it.currencyPairLabel == currencyPair.label)
                            emit(it)
                    }
                } catch (x: CancellationException) {
                    // TODO: Check will it really be called? coroutine cancellation might prevent it from normal run
                    RealtimeChannel.watchPair(currencyPair, false)
                }
            }
        }

        suspend fun getLastStick(currencyPair: CurrencyPair,rate: StatSampleRate): PushPayload.JumpingCandleStick =
            RealtimeChannel.getLastStick(currencyPair,rate)

        /**
         * The flow of candelesticks including, when possible, incomplete, dynamic sick as first element (in which
         * case there will be length + 1 elements in returned list), or just list of complete sticks if there is
         * no data ready. Is expermental and intended to replace [lastStickFlowOf] and [flowOf].
         */
        suspend fun sticksFlow(currencyPair: CurrencyPair, rate: StatSampleRate, length: Int)
                : Flow<List<Pair<Instant, CandleStick>>> {
            return flow {
                coroutineScope {
                    var last: PushPayload.JumpingCandleStick? = null
                    val pulser = Channel<Int>(1)
                    val job1 = launch {
                        lastStickFlowOf(currencyPair, rate).collect {
                            last = it
                            pulser.send(1)
                        }
                    }
                    var data: List<Pair<Instant, CandleStick>>? = null
                    val job2 = globalLaunch {
                        flowOf(currencyPair, rate, length).collect {
                            data = it
                            pulser.send(2)
                        }
                    }
                    val job3 = launch {
                        GuestAPI().loggedInFlow.collect {
                            if( it ) {
                                last = RealtimeChannel.getLastStick(currencyPair, rate)
                                data = Stats(currencyPair,rate,length).load(Now(), length)
                                pulser.send(3)
                            }
                        }

                    }
                    while (!pulser.isClosedForSend) {
                        val code = pulser.receive()
                        val result = data
                        if (result != null) {
                            // only when there are data
                            val firstStick = last
                            if (firstStick != null) {
                                // if first stick present it may, or may not be adjacent to a data:
                                val p1 = rate.unit.periodContaining(firstStick.startsAt)
                                val p2 = rate.unit.periodContaining(result[0].first)
                                debug { "jumping stick: $p1 firrst stick $p2" }
                                if (p1.startsAt == p2.endsAt) {
                                    debug { "jumping stuck is adjacent to our stats, so we add it" }
                                }
                                val extendedResult = mutableListOf<Pair<Instant, CandleStick>>()
                                extendedResult.add(firstStick.startsAt to firstStick.candleStick)
                                extendedResult.addAll(result)
                                emit(extendedResult)
                            } else {
                                debug { "first stick is not adjacent to collected stat data and will be ignored" }
                                emit(result)
                            }
                        } else {
                            debug { "jumping stick arrived ahead of stats, waiting for stats" }
                        }
                    }
                    job1.cancel()
                    job2.cancel()
                    job3.cancel()
                }
            }
        }
    }
}

enum class StatSampleRate(val unit: StatTimeUnit) {
    REALTIME(DurationTimeUnit(1.seconds)),
    MINUTES(DurationTimeUnit(1.minutes)),
    HOURS(DurationTimeUnit(1.hours)),
    DAYS(DayStatTimeUnit())
}

interface StatTimeUnit {
    fun periodContaining(time: Instant): Period
    fun nextPeriod(time: Instant): Period

    fun closestInPast(): Period = nextPeriod(Clock.System.now())
}

class Period internal constructor(
    val startsAt: Instant,
    val endsAt: Instant,
) : Comparable<Period> {

    constructor(time: Instant, unit: StatTimeUnit) :
            this(unit.periodContaining(time).startsAt, unit.nextPeriod(time).startsAt)

    val inFuture by lazy { endsAt > Clock.System.now() }

    @Suppress("unused")
    val beforeEra = beforeEra(startsAt)


    override fun compareTo(other: Period): Int {
        val cmp = startsAt.compareTo(other.startsAt)
        return if (cmp == 0) endsAt.compareTo(other.endsAt) else cmp
    }

    companion object {
        val era = Instant.parse("2022-03-20T00:00:00Z")

        fun beforeEra(instant: Instant) = instant < era
    }

    override fun toString(): String {
        return "%tO...%tO".format(startsAt, endsAt)
    }

    override fun hashCode(): Int {
        return Hash.combine(startsAt, endsAt)
    }

    override fun equals(other: Any?): Boolean {
        return other is Period && compareTo(other) == 0
    }
}

class DurationTimeUnit(val duration: Duration) : StatTimeUnit {
    override fun periodContaining(time: Instant): Period {
        val t = time.epochSeconds
        val d = duration.inWholeSeconds
        val startSeconds = t - (t % d)
        val start = Instant.fromEpochSeconds(startSeconds, 0)
        val end = Instant.fromEpochSeconds(startSeconds + d, 0)
        return Period(start, end)
    }

    override fun nextPeriod(time: Instant): Period = periodContaining(time - duration)
}

class DayStatTimeUnit : StatTimeUnit {
    override fun periodContaining(time: Instant): Period {
        val date = time.toLocalDateTime(TimeZone.UTC).date
        return Period(date.atStartOfDayIn(TimeZone.UTC),
            date.plus(1, DateTimeUnit.DAY).atStartOfDayIn(TimeZone.UTC))
    }

    override fun nextPeriod(time: Instant): Period {
        val date = time.toLocalDateTime(TimeZone.UTC).date.minus(1, DateTimeUnit.DAY)
        return Period(date.atStartOfDayIn(TimeZone.UTC),
            date.plus(1, DateTimeUnit.DAY).atStartOfDayIn(TimeZone.UTC))
    }
}
